import type { MedplumClient } from '@medplum/core';
import { useMedplum } from '@medplum/react';
import type { EpisodeOfCare, Patient, RelatedPerson } from '@medplum/fhirtypes';
import { useCallback, useEffect, useState } from 'react';
import { getGuarantorRef, getParticipantRefs, getServiceTypeCode } from '@unify/fhir-model/resources/episode-of-care';
import { displayLabelForEpisodeType } from '../lib/fhirValueSets/sessionAndEpisodeTypes';
import { patientCompartmentSearch } from '../lib/patientCoverageSearch';
import { patientContactToContact } from '../pages/clients/utils/clientProfileFhir';
import { contactDisplayEmail, contactDisplayName, contactDisplayPhone } from '../pages/clients/utils/contactDisplayUtils';
import type { ClientOverviewContactRow, ClientOverviewContactSegment } from '../pages/clients/utils/getClientOverviewContacts';

/** Avoid silently truncating RelatedPerson / EpisodeOfCare bundles on busy charts. */
const CHART_CONTACTS_SEARCH_COUNT = '500';

export const PATIENT_CONTACT_CHART_LABEL = 'Patient.contact (chart)';
export const OUTSIDE_EPISODES_OF_CARE_LABEL = 'Outside episodes of care';

function normalizePhoneDigits(value: string | undefined): string | undefined {
  const d = value?.replace(/\D/g, '') ?? '';
  return d.length > 0 ? d : undefined;
}

function normalizeEmail(value: string | undefined): string | undefined {
  const t = value?.trim().toLowerCase();
  return t || undefined;
}

function patientSharesTelecomWithRelatedPerson(patient: Patient, rp: RelatedPerson): boolean {
  const rpPhones = new Set(
    (rp.telecom ?? [])
      .filter((t) => t.system === 'phone')
      .map((t) => normalizePhoneDigits(t.value))
      .filter((x): x is string => Boolean(x)),
  );
  const rpEmails = new Set(
    (rp.telecom ?? [])
      .filter((t) => t.system === 'email')
      .map((t) => normalizeEmail(t.value))
      .filter((x): x is string => Boolean(x)),
  );
  if (rpPhones.size === 0 && rpEmails.size === 0) return false;

  for (const t of patient.telecom ?? []) {
    if (t.system === 'phone') {
      const d = normalizePhoneDigits(t.value);
      if (d && rpPhones.has(d)) return true;
    }
    if (t.system === 'email') {
      const e = normalizeEmail(t.value);
      if (e && rpEmails.has(e)) return true;
    }
  }
  return false;
}

/**
 * Resolves RelatedPerson → another Patient's chart id for Medical Record contact navigation.
 *
 * `Patient?link=RelatedPerson/{id}` also returns the **subject** client when their `Patient.link`
 * lists that RelatedPerson (family/couples directory). Those hits must be excluded in favor of
 * the linked person's own Patient (portal identity) or a pilot-tagged name match.
 */
export function pickLinkedClientPatientIdForRelatedPerson(
  linkSearchPatients: Patient[],
  nameSearchPatients: Patient[] | undefined,
  viewingPatientId: string,
  relatedPerson: RelatedPerson,
): string | undefined {
  const fromLink = linkSearchPatients.find((p) => p.id && p.id !== viewingPatientId);
  if (fromLink?.id) return fromLink.id;

  const nameCands = (nameSearchPatients ?? []).filter((p) => p.id && p.id !== viewingPatientId);
  if (nameCands.length === 0) return undefined;
  if (nameCands.length === 1) return nameCands[0].id;

  const byTelecom = nameCands.filter((p) => patientSharesTelecomWithRelatedPerson(p, relatedPerson));
  if (byTelecom.length === 1) return byTelecom[0].id;
  return undefined;
}

/** Resolve RelatedPerson ids → linked client Patient ids (when the partner has their own chart). */
export async function resolveRelatedPersonToLinkedChartPatientIds(
  medplum: MedplumClient,
  relatedPersons: RelatedPerson[],
  viewingPatientId: string,
): Promise<Map<string, string>> {
  const entries = await Promise.all(
    relatedPersons.map(async (rp) => {
      if (rp.active === false || !rp.id) return null;

      const linkPatients = await medplum
        .searchResources('Patient', { link: `RelatedPerson/${rp.id}` })
        .catch(() => [] as Patient[]);

      let namePatients: Patient[] | undefined;
      const n = rp.name?.[0];
      const given0 = n?.given?.[0]?.trim();
      const family = n?.family?.trim();
      if (given0 && family) {
        namePatients = await medplum
          .searchResources('Patient', {
            given: given0,
            family,
            _count: '25',
          })
          .catch(() => [] as Patient[]);
      }

      const chartId = pickLinkedClientPatientIdForRelatedPerson(
        linkPatients,
        namePatients,
        viewingPatientId,
        rp,
      );
      return chartId ? ([rp.id, chartId] as const) : null;
    }),
  );

  const rpToPatient = new Map<string, string>();
  for (const e of entries) {
    if (e) rpToPatient.set(e[0], e[1]);
  }
  return rpToPatient;
}

/**
 * FHIR-backed contact rows for one patient (Patient.contact, RelatedPerson, EoC guarantor),
 * using the same rules as Client Profile → Contacts and Medical Record sidebar.
 */
export async function fetchMedplumPatientContactRows(
  medplum: MedplumClient,
  patientId: string,
): Promise<ClientOverviewContactRow[]> {
  const patientRef = `Patient/${patientId}`;
  const emptyPatient = { resourceType: 'Patient', id: patientId } as Patient;
  const compartment = patientCompartmentSearch(patientId);
  const [patient, relatedPersons, eocs] = await Promise.all([
    medplum.readResource('Patient', patientId).catch(() => emptyPatient),
    medplum
      .searchResources('RelatedPerson', {
        ...compartment,
        _count: CHART_CONTACTS_SEARCH_COUNT,
      })
      .catch(() =>
        medplum.searchResources('RelatedPerson', {
          patient: patientRef,
          _count: CHART_CONTACTS_SEARCH_COUNT,
        }),
      ),
    medplum
      .searchResources('EpisodeOfCare', {
        ...compartment,
        _count: CHART_CONTACTS_SEARCH_COUNT,
      })
      .catch(() =>
        medplum.searchResources('EpisodeOfCare', {
          patient: patientRef,
          _count: CHART_CONTACTS_SEARCH_COUNT,
        }),
      ),
  ]);

  const rpToPatient = await resolveRelatedPersonToLinkedChartPatientIds(
    medplum,
    relatedPersons,
    patientId,
  );

  return buildContactRows(patient, relatedPersons, eocs, patientId, rpToPatient);
}

export interface CaseloadAggregatedContactRow extends ClientOverviewContactRow {
  subjectPatientId: string;
  subjectClientName: string;
  rowKey: string;
}

/**
 * Loads and flattens contact rows for every caseload client (same Patient ids as All tab source).
 */
export function useMedplumCaseloadAggregatedContacts(clients: { id: string; name: string }[]): {
  rows: CaseloadAggregatedContactRow[];
  loading: boolean;
} {
  const medplum = useMedplum();
  const [rows, setRows] = useState<CaseloadAggregatedContactRow[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (clients.length === 0) {
      setRows([]);
      setLoading(false);
      return;
    }
    let cancelled = false;
    (async () => {
      setLoading(true);
      setRows([]);
      try {
        const batches = await Promise.all(
          clients.map(async (c) => {
            const contacts = await fetchMedplumPatientContactRows(medplum, c.id).catch(() => [] as ClientOverviewContactRow[]);
            return contacts.map(
              (row): CaseloadAggregatedContactRow => ({
                ...row,
                subjectPatientId: c.id,
                subjectClientName: c.name,
                rowKey: `${c.id}:${row.dedupeKey}`,
              }),
            );
          }),
        );
        if (cancelled) return;
        const flat = batches.flat();
        flat.sort((a, b) => {
          const byClient = a.subjectClientName.localeCompare(b.subjectClientName);
          if (byClient !== 0) return byClient;
          return a.displayName.localeCompare(b.displayName);
        });
        setRows(flat);
      } catch {
        if (!cancelled) setRows([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [medplum, clients]);

  return { rows, loading };
}

/**
 * Loads Medical Record contacts from the same sources as Client Profile → Contacts:
 * every `Patient.contact[]` row, every active `RelatedPerson` for the patient (no couples/family
 * co-member filtering), and Episode guarantor extensions.
 */
export function useMedplumPatientContacts(
  patientId: string | undefined,
): {
  contacts: ClientOverviewContactRow[];
  loading: boolean;
} {
  const medplum = useMedplum();
  const [contacts, setContacts] = useState<ClientOverviewContactRow[]>([]);
  const [loading, setLoading] = useState(false);

  const fetchContacts = useCallback(async () => {
    if (!patientId) {
      setContacts([]);
      return;
    }
    setLoading(true);
    try {
      const rows = await fetchMedplumPatientContactRows(medplum, patientId);
      setContacts(rows);
    } catch {
      setContacts([]);
    } finally {
      setLoading(false);
    }
  }, [medplum, patientId]);

  useEffect(() => {
    fetchContacts();
  }, [fetchContacts]);

  return { contacts, loading };
}

function relatedPersonName(rp: RelatedPerson): string {
  const n = rp.name?.[0];
  if (!n) return 'Related Person';
  return [n.prefix?.[0], ...(n.given ?? []), n.family].filter(Boolean).join(' ').trim() || 'Related Person';
}

function relationshipLabel(rp: RelatedPerson): string {
  const coding = rp.relationship?.[0]?.coding?.[0];
  if (coding?.display) return coding.display;
  if (coding?.code) {
    const map: Record<string, string> = {
      PRN: 'Parent', FTH: 'Father', MTH: 'Mother', SPS: 'Spouse',
      GUARD: 'Guardian', SIB: 'Sibling', CHILD: 'Child', FRND: 'Friend',
      DOMPART: 'Domestic Partner',
    };
    return map[coding.code] ?? coding.code;
  }
  return 'Related';
}

type ContactRowAcc = {
  dedupeKey: string;
  displayName: string;
  segments: ClientOverviewContactSegment[];
  clientChartId?: string;
  isGuarantor: boolean;
  phone?: string;
  email?: string;
};

function buildContactRows(
  patient: Patient,
  relatedPersons: RelatedPerson[],
  eocs: EpisodeOfCare[],
  viewingPatientId: string,
  rpToPatient: Map<string, string>,
): ClientOverviewContactRow[] {
  const byKey = new Map<string, ContactRowAcc>();

  (patient.contact ?? []).forEach((pc, index) => {
    const c = patientContactToContact(pc, index);
    const dedupeKey = `pc:${index}`;
    let displayName = contactDisplayName(c);
    if (displayName === '—' && pc.name?.text?.trim()) {
      displayName = pc.name.text.trim();
    }
    const roleLabel = c.relationshipType;
    const segment: ClientOverviewContactSegment = {
      roleLabel,
      episodeOfCareLabel: PATIENT_CONTACT_CHART_LABEL,
    };
    byKey.set(dedupeKey, {
      dedupeKey,
      displayName,
      segments: [segment],
      clientChartId: undefined,
      isGuarantor: false,
      phone: contactDisplayPhone(c) || undefined,
      email: contactDisplayEmail(c) || undefined,
    });
  });

  for (const rp of relatedPersons) {
    if (rp.active === false) {
      continue;
    }

    const name = relatedPersonName(rp);
    const role = relationshipLabel(rp);
    const eocSegments = findEocSegmentsForRelatedPerson(rp, eocs);
    const segmentsToAdd =
      eocSegments.length > 0
        ? eocSegments
        : [{ roleLabel: role, episodeOfCareLabel: OUTSIDE_EPISODES_OF_CARE_LABEL }];

    const key = rp.id ? `rp:${rp.id}` : `name:${name.toLowerCase()}`;
    const existing = byKey.get(key);
    const resolvedPatientId = rp.id ? rpToPatient.get(rp.id) : undefined;
    const phone = rp.telecom?.find((t) => t.system === 'phone')?.value;
    const email = rp.telecom?.find((t) => t.system === 'email')?.value;

    if (existing) {
      for (const segment of segmentsToAdd) {
        const segKey = `${segment.roleLabel}\0${segment.episodeOfCareLabel}`;
        if (!existing.segments.some((s) => `${s.roleLabel}\0${s.episodeOfCareLabel}` === segKey)) {
          existing.segments.push(segment);
        }
      }
      if (!existing.clientChartId && resolvedPatientId) {
        existing.clientChartId = resolvedPatientId;
      }
      if (!existing.phone && phone) existing.phone = phone;
      if (!existing.email && email) existing.email = email;
    } else {
      byKey.set(key, {
        dedupeKey: key,
        displayName: name,
        segments: [...segmentsToAdd],
        clientChartId: resolvedPatientId,
        isGuarantor: false,
        phone,
        email,
      });
    }
  }

  for (const eoc of eocs) {
    const guarantorExt = eoc.extension?.find((e) => e.url === 'https://unify.health/fhir/guarantor');
    if (!guarantorExt?.valueReference) continue;

    const name = guarantorExt.valueReference.display ?? 'Guarantor';
    const ref = guarantorExt.valueReference.reference ?? '';
    const eocName = displayLabelForEpisodeType(eoc.type?.[0]);

    const key = ref ? `ref:${ref}` : `name:${name.toLowerCase()}`;
    const segment: ClientOverviewContactSegment = { roleLabel: 'Guarantor', episodeOfCareLabel: eocName };

    const existing = byKey.get(key);
    if (existing) {
      const segKey = `${segment.roleLabel}\0${segment.episodeOfCareLabel}`;
      if (!existing.segments.some((s) => `${s.roleLabel}\0${s.episodeOfCareLabel}` === segKey)) {
        existing.segments.push(segment);
      }
      existing.isGuarantor = true;
    } else {
      let merged = false;
      for (const [, v] of byKey) {
        if (v.dedupeKey.startsWith('pc:')) continue;
        if (v.displayName.toLowerCase() === name.toLowerCase()) {
          v.segments.push(segment);
          v.isGuarantor = true;
          merged = true;
          break;
        }
      }
      if (!merged) {
        byKey.set(key, {
          dedupeKey: key,
          displayName: name,
          segments: [segment],
          clientChartId: undefined,
          isGuarantor: true,
        });
      }
    }
  }

  const mergedByName = new Map<string, ContactRowAcc>();
  for (const row of byKey.values()) {
    const norm = row.displayName.trim().toLowerCase();
    const existing = mergedByName.get(norm);
    if (!existing) {
      mergedByName.set(norm, row);
      continue;
    }
    for (const seg of row.segments) {
      const segKey = `${seg.roleLabel}\0${seg.episodeOfCareLabel}`;
      if (!existing.segments.some((s) => `${s.roleLabel}\0${s.episodeOfCareLabel}` === segKey)) {
        existing.segments.push(seg);
      }
    }
    existing.phone = existing.phone || row.phone;
    existing.email = existing.email || row.email;
    if (!existing.clientChartId && row.clientChartId) existing.clientChartId = row.clientChartId;
    existing.isGuarantor = existing.isGuarantor || row.isGuarantor;
  }

  return [...mergedByName.values()]
    .map((m) => ({
      dedupeKey: m.dedupeKey,
      displayName: m.displayName,
      rolesSummary: summarizeRoles(m.segments),
      episodesOfCareSummary: m.segments.map((s) => `${s.roleLabel} — ${s.episodeOfCareLabel}`).join(', '),
      segments: m.segments,
      clientChartId: m.clientChartId,
      guarantorBillingProfileClientId: m.isGuarantor ? viewingPatientId : undefined,
      phone: m.phone,
      email: m.email,
    }))
    .sort((a, b) => a.displayName.localeCompare(b.displayName));
}

function isCouplesPartnerRelatedPerson(rp: RelatedPerson): boolean {
  return (rp.relationship ?? []).some((rel) =>
    (rel.coding ?? []).some(
      (c) =>
        c.code === 'SPS'
        || c.code === 'DOMPART'
        || (c.display?.toLowerCase().includes('spouse') ?? false)
        || (c.display?.toLowerCase().includes('partner') ?? false),
    ),
  );
}

const FAMILY_MEMBER_RELATIONSHIP_CODES = new Set(['PRN', 'FTH', 'MTH', 'SIB', 'CHILD', 'GUARD']);

function isFamilyMemberRelatedPerson(rp: RelatedPerson): boolean {
  return (rp.relationship ?? []).some((rel) =>
    (rel.coding ?? []).some((c) => c.code && FAMILY_MEMBER_RELATIONSHIP_CODES.has(c.code)),
  );
}

function episodeTherapyTypeCode(eoc: EpisodeOfCare): string | undefined {
  const fromModel = getServiceTypeCode(eoc);
  if (fromModel) return fromModel;
  const type = eoc.type?.[0];
  const codingCode = type?.coding?.find((c) => c.code)?.code;
  if (codingCode) return codingCode;
  const text = type?.text?.toLowerCase() ?? '';
  if (text.includes('couples')) return 'couples';
  if (text.includes('family')) return 'family';
  if (text.includes('group')) return 'group';
  return undefined;
}

function findEocSegmentsForRelatedPerson(rp: RelatedPerson, eocs: EpisodeOfCare[]): ClientOverviewContactSegment[] {
  const role = relationshipLabel(rp);
  const rpRef = rp.id ? `RelatedPerson/${rp.id}` : undefined;
  const segments: ClientOverviewContactSegment[] = [];
  const seen = new Set<string>();

  for (const eoc of eocs) {
    const eocName = displayLabelForEpisodeType(eoc.type?.[0]);
    const typeCode = episodeTherapyTypeCode(eoc);
    let segment: ClientOverviewContactSegment | undefined;

    if (rpRef && getParticipantRefs(eoc).some((ref) => ref.reference === rpRef)) {
      segment = { roleLabel: role, episodeOfCareLabel: eocName };
    } else {
      const guarantorRef = getGuarantorRef(eoc)?.reference;
      if (rpRef && guarantorRef === rpRef) {
        segment = { roleLabel: 'Guarantor', episodeOfCareLabel: eocName };
      } else if (typeCode === 'couples' && isCouplesPartnerRelatedPerson(rp)) {
        segment = { roleLabel: role, episodeOfCareLabel: eocName };
      } else if (typeCode === 'family' && isFamilyMemberRelatedPerson(rp)) {
        segment = { roleLabel: role, episodeOfCareLabel: eocName };
      }
    }

    if (!segment) continue;
    const segKey = `${segment.roleLabel}\0${segment.episodeOfCareLabel}`;
    if (seen.has(segKey)) continue;
    seen.add(segKey);
    segments.push(segment);
  }

  return segments;
}

function summarizeRoles(segments: ClientOverviewContactSegment[]): string {
  const seen = new Set<string>();
  const ordered: string[] = [];
  for (const s of segments) {
    if (seen.has(s.roleLabel)) continue;
    seen.add(s.roleLabel);
    ordered.push(s.roleLabel);
  }
  return ordered.join(', ') || '—';
}
